import React, { useEffect, useState } from "react";
import { api, UNIT_META } from "@/lib/api";
import { PageHeader, Panel, EmptyState } from "@/components/ui-parts";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { DialogShell, Field, Input, Textarea, UnitSelect } from "@/components/form-helpers";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Electricity() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    unit: "", date: todayISO(), opening_reading: "", closing_reading: "", rate_per_unit: "", dg_units: "", remarks: "",
  });

  const load = async () => {
    const r = await api.get("/electricity");
    setRows(r.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/electricity", {
        ...form,
        opening_reading: Number(form.opening_reading) || 0,
        closing_reading: Number(form.closing_reading) || 0,
        rate_per_unit: Number(form.rate_per_unit) || 0,
        dg_units: Number(form.dg_units) || 0,
      });
      toast.success("Electricity reading saved");
      setOpen(false);
      setForm({ unit: "", date: todayISO(), opening_reading: "", closing_reading: "", rate_per_unit: "", dg_units: "", remarks: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Data Entry"
        title="Electricity Usage"
        description="Daily meter readings, DG usage and computed cost per unit."
        testid="electricity-header"
        actions={
          <Button data-testid="add-electricity-btn" onClick={() => setOpen(true)} className="bg-amber-400 hover:bg-amber-300 text-black rounded-none">
            <Plus className="mr-2" size={14} weight="bold" /> New Reading
          </Button>
        }
      />

      <Panel title={`Meter Readings (${rows.length})`}>
        {rows.length === 0 ? (
          <EmptyState message="No electricity readings yet" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800">
                <TableHead className="label-caps">Date</TableHead>
                <TableHead className="label-caps">Unit</TableHead>
                <TableHead className="label-caps text-right">Opening</TableHead>
                <TableHead className="label-caps text-right">Closing</TableHead>
                <TableHead className="label-caps text-right">Consumed (kWh)</TableHead>
                <TableHead className="label-caps text-right">DG (kWh)</TableHead>
                <TableHead className="label-caps text-right">Rate</TableHead>
                <TableHead className="label-caps text-right">Cost (₹)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="border-zinc-800" data-testid={`elec-row-${r.id}`}>
                  <TableCell className="font-mono text-xs">{r.date}</TableCell>
                  <TableCell><span className="label-caps" style={{ color: UNIT_META[r.unit]?.accent }}>{UNIT_META[r.unit]?.tag}</span></TableCell>
                  <TableCell className="text-right font-mono">{r.opening_reading}</TableCell>
                  <TableCell className="text-right font-mono">{r.closing_reading}</TableCell>
                  <TableCell className="text-right font-mono text-amber-400">{r.units_consumed}</TableCell>
                  <TableCell className="text-right font-mono">{r.dg_units}</TableCell>
                  <TableCell className="text-right font-mono">{r.rate_per_unit}</TableCell>
                  <TableCell className="text-right font-mono">{r.total_cost?.toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>

      <DialogShell open={open} onOpenChange={setOpen} title="Meter Reading" onSubmit={submit} submitLabel="Save Reading" testidSubmit="save-electricity-btn">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Unit"><UnitSelect testid="elec-unit" value={form.unit} onChange={(v) => setForm({ ...form, unit: v })} /></Field>
          <Field label="Date"><Input data-testid="elec-date" type="date" className="rounded-none bg-zinc-950 border-zinc-800" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
          <Field label="Opening Reading"><Input data-testid="elec-opening" type="number" step="0.01" className="rounded-none bg-zinc-950 border-zinc-800" value={form.opening_reading} onChange={(e) => setForm({ ...form, opening_reading: e.target.value })} /></Field>
          <Field label="Closing Reading"><Input data-testid="elec-closing" type="number" step="0.01" className="rounded-none bg-zinc-950 border-zinc-800" value={form.closing_reading} onChange={(e) => setForm({ ...form, closing_reading: e.target.value })} /></Field>
          <Field label="Rate ₹/kWh"><Input data-testid="elec-rate" type="number" step="0.01" className="rounded-none bg-zinc-950 border-zinc-800" value={form.rate_per_unit} onChange={(e) => setForm({ ...form, rate_per_unit: e.target.value })} /></Field>
          <Field label="DG Units (kWh)"><Input data-testid="elec-dg" type="number" step="0.01" className="rounded-none bg-zinc-950 border-zinc-800" value={form.dg_units} onChange={(e) => setForm({ ...form, dg_units: e.target.value })} /></Field>
        </div>
        <Field label="Remarks"><Textarea data-testid="elec-remarks" className="rounded-none bg-zinc-950 border-zinc-800" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field>
      </DialogShell>
    </div>
  );
}
